
import React, { useEffect, useState } from 'react';
import { socketService } from '../../../../services/socketService';

interface OnlineUser {
    id: string; // socketId
    userId: string;
    page: string | null;
    title: string | null;
    userAgent: string;
    ip: string;
    lastActive: number;
    timestamp: number;
}

const RealTimeMonitor: React.FC<{ onStartChat?: (userId: string, socketId: string) => void }> = ({ onStartChat }) => {
    const [users, setUsers] = useState<OnlineUser[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Ensure connected
        socketService.connect();

        socketService.onUserListUpdate((updatedUsers) => {
            setUsers(updatedUsers);
            setLoading(false);
        });

        // Cleanup? Socket service is singleton, maybe just remove listener if possible
        // but socketService.off logic is simple.
        return () => {
            socketService.off('users_online');
        };
    }, []);

    const getDeviceIcon = (ua: string) => {
        if (!ua) return '💻';
        if (ua.includes('Mobile')) return '📱';
        return '💻';
    };

    const getBrowserName = (ua: string) => {
        if (!ua) return 'Unknown';
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        return 'Other';
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                <div>
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2">
                        <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                        </span>
                        Real-Time Traffic
                    </h3>
                    <p className="text-xs text-gray-500">Live visitors on the site right now</p>
                </div>
                <div className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">
                    {users.length} Active Users
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {users.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-60">
                        <div className="text-6xl mb-4">🌍</div>
                        <p>Waiting for visitors...</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {users.map((user) => (
                            <div key={user.id} className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow flex justify-between items-center group">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xl">
                                        {getDeviceIcon(user.userAgent)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-sm text-gray-900 dark:text-white">{user.userId}</span>
                                            <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded">
                                                {getBrowserName(user.userAgent)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                                            <span className="material-icons-round text-[10px]">visibility</span>
                                            {user.page || 'Home'}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="text-right mr-2 hidden md:block">
                                        <div className="text-[10px] text-gray-400">Active since</div>
                                        <div className="text-xs font-mono">{new Date(user.timestamp).toLocaleTimeString()}</div>
                                    </div>
                                    <button
                                        onClick={() => onStartChat(user.userId, user.id)}
                                        className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        Message
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RealTimeMonitor;
